import React, { useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { useNavigate } from 'react-router-dom';
import PlayerName, { usePlayerDecorations } from '../Common/PlayerName.tsx';
import './WeeklyMode.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

const WeeklyHub: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Fetch status
  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/weekly/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      } else {
        setError('Erreur chargement Weekly');
      }
    } catch (e) {
      console.error('Weekly status error:', e);
      setError('Erreur réseau');
    }
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Leaderboard decorations
  const leaderboardUserIds = status?.leaderboard?.map((r: any) => r.userId) || [];
  const { decorations } = usePlayerDecorations(leaderboardUserIds);

  // Countdown timer
  const [endCountdown, setEndCountdown] = useState('');
  const [regenCountdown, setRegenCountdown] = useState('');

  // Calculer le prochain lundi minuit (timezone locale)
  const getNextMondayMidnight = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0=dimanche, 1=lundi, ..., 6=samedi
    
    // Jours jusqu'au prochain lundi
    const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay) % 7 || 7;
    
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(0, 0, 0, 0);
    
    return nextMonday.getTime();
  };

  // Mettre à jour les countdowns toutes les secondes
  useEffect(() => {
    function updateCountdowns() {
      const now = Date.now();

      // Countdown jusqu'au prochain lundi minuit
      const nextMondayTime = getNextMondayMidnight();
      const diff = Math.max(0, Math.floor((nextMondayTime - now) / 1000));
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      setEndCountdown(`${d}j ${h}h ${m}m`);

      // Regen countdown
      if (status?.lives?.nextRegenIn > 0 && status.lives.remaining < status.lives.max) {
        const h = Math.floor(status.lives.nextRegenIn / 3600);
        const m = Math.floor((status.lives.nextRegenIn % 3600) / 60);
        setRegenCountdown(`${h}h ${m}m`);
      } else {
        setRegenCountdown('');
      }
    }

    // Calcul initial
    updateCountdowns();

    // Mise à jour toutes les secondes
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [status]);

  if (loading) {
    return <div className="weekly-container" style={{ textAlign: 'center', paddingTop: 80 }}>Chargement...</div>;
  }

  if (error) {
    return (
      <div className="weekly-container" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😵</div>
        <div style={{ color: '#ef4444' }}>{error}</div>
        <button className="weekly-back-btn" onClick={() => navigate('/')} style={{ marginTop: 16 }}>← Retour</button>
      </div>
    );
  }

  if (!status) return null;

  const {
    season, currentDay, currentTheme, currentThemeEmoji,
    lives, playerStats, todayProgress, leaderboard: lb, calendar
  } = status;

  const canPlay = lives.remaining > 0 && !todayProgress.completed;
  const multiplier = playerStats.multiplier;
  const nextMulti = playerStats.nextMultiplier;

  return (
    <div className="weekly-container">
      <button className="weekly-back-btn" onClick={() => navigate('/')}>← Retour</button>

      {/* Title */}
      <div className="weekly-hub-title">
        <h1>🏆 MODE WEEKLY</h1>
      </div>
      <div className="weekly-hub-subtitle">
        Semaine #{season.seasonNumber}
      </div>

      {/* Today theme */}
      <div className="weekly-today-card">
        <div className="weekly-today-label">Thème du jour — {calendar?.find((c: any) => c.isCurrent)?.label || currentDay}</div>
        <div className="weekly-today-theme-emoji">{currentThemeEmoji}</div>
        <div className="weekly-today-theme">{currentTheme}</div>
        {todayProgress.totalAvailable > 0 && (
          <div className="weekly-today-progress-text">
            {todayProgress.questionsAnswered} / {todayProgress.totalAvailable} questions
          </div>
        )}
        {todayProgress.completed && (
          <div className="weekly-today-completed">✅ Thème du jour terminé !</div>
        )}
      </div>

      {/* Lives */}
      <div className="weekly-lives-container">
        <div className="weekly-lives">
          {Array.from({ length: lives.max }).map((_, i) => (
            <span
              key={i}
              className={`weekly-life-heart ${
                i < lives.remaining
                  ? lives.remaining === 1 ? 'critical' : 'active'
                  : 'empty'
              }`}
            >
              ❤️
            </span>
          ))}
        </div>
        {lives.remaining < lives.max && (
          <span className="weekly-lives-regen">+1 dans {regenCountdown || '...'}</span>
        )}
      </div>

      {/* Stats */}
      <div className="weekly-stats-bar">
        <div className="weekly-stat-item">
          <div className="weekly-stat-value">{playerStats.rawScore}</div>
          <div className="weekly-stat-label">Score brut</div>
          <div className="weekly-stat-sub">×{multiplier} → {playerStats.estimatedScore}</div>
        </div>
        <div className="weekly-stat-item">
          <div className="weekly-stat-value">{playerStats.daysPlayed}/7</div>
          <div className="weekly-stat-label">Jours joués</div>
          <div className="weekly-stat-sub">🔥</div>
        </div>
        <div className="weekly-stat-item">
          <div className="weekly-stat-value">
            {playerStats.rank ? (
              <span className="weekly-rank-indicator">
                {playerStats.rank <= 3
                  ? ['🥇', '🥈', '🥉'][playerStats.rank - 1]
                  : `#${playerStats.rank}`}
              </span>
            ) : '-'}
          </div>
          <div className="weekly-stat-label">Classement</div>
          <div className="weekly-stat-sub">🌊 Vague {playerStats.currentWave}</div>
        </div>
      </div>

      {/* Multiplier hint */}
      {nextMulti && (
        <div className="weekly-multiplier-hint">
          💡 Joue {nextMulti.daysNeeded} jour{nextMulti.daysNeeded > 1 ? 's' : ''} de plus pour ×{nextMulti.nextMultiplier} !
        </div>
      )}

      {/* Play button */}
      <button
        className={`weekly-play-btn ${canPlay ? 'active' : 'disabled'}`}
        onClick={() => canPlay && navigate('/weekly/play')}
        disabled={!canPlay}
      >
        {todayProgress.completed
          ? '✅ Thème terminé — Reviens demain !'
          : lives.remaining <= 0
            ? `💀 Plus de vies — ${regenCountdown || '...'}`
            : `▶️ Jouer (${currentThemeEmoji} ${currentTheme})`
        }
      </button>

      {/* Calendar */}
      <div className="weekly-calendar">
        {(calendar || []).map((dayInfo: any) => (
          <div
            key={dayInfo.day}
            className={`weekly-calendar-day ${dayInfo.played ? 'played' : ''} ${dayInfo.isCurrent ? 'current' : ''} ${dayInfo.isFuture ? 'future' : ''}`}
          >
            <div className="weekly-calendar-day-label">{dayInfo.label}</div>
            <div className="weekly-calendar-day-emoji">{dayInfo.themeEmoji}</div>
            <div className="weekly-calendar-day-status">
              {dayInfo.played ? '✅' : dayInfo.isFuture ? '🔒' : dayInfo.isCurrent ? '🔵' : '⚪'}
            </div>
          </div>
        ))}
      </div>

      {/* Countdown */}
      <div className="weekly-countdown">
        📜 Prochain weekly dans <strong>{endCountdown || '...'}</strong>
      </div>

      {/* Leaderboard */}
      <div className="weekly-leaderboard">
        <div className="weekly-leaderboard-title">📊 Classement · Top 50</div>
        <div className="weekly-leaderboard-list">
          {lb && lb.length > 0 ? (
            lb.map((entry: any) => {
              const isMe = entry.userId === user?.uid;
              const rankClass = entry.rank === 1 ? 'top1' : entry.rank === 2 ? 'top2' : entry.rank === 3 ? 'top3' : '';
              return (
                <div key={entry.userId} className={`weekly-leaderboard-row ${isMe ? 'is-me' : ''}`}>
                  <span className={`weekly-lb-rank ${rankClass}`}>
                    {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                  </span>
                  <span className="weekly-lb-name">
                    <PlayerName
                      userId={entry.userId}
                      userName={entry.userName}
                      decoration={decorations?.[entry.userId]}
                    />
                  </span>
                  <span className="weekly-lb-score">{entry.estimatedScore} pts</span>
                  <span className="weekly-lb-details">
                    ({entry.rawScore}×{entry.multiplier})
                  </span>
                  <span className="weekly-lb-days">🔥{entry.daysPlayed}j</span>
                </div>
              );
            })
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
              Aucun joueur cette semaine — Sois le premier ! 🚀
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyHub;
